import * as React from "react";
import {
  IconReactionMustHug,
  IconReactionNah,
  IconReactionYay,
  IconReactionMeh,
} from "atoms";
import { useProject } from "features/reviewable/api/useProject";
import { useReviews } from "../api/useReviews";
import { addOrIncrement, sortTagsMap } from "../utils";
import { ReviewStats } from "./ReviewStats";

interface Props {
  projectId: string;
}

function ProjectReviewStats({ projectId, ...props }: Props) {
  const { data: reviewsResponse } = useReviews("PROJECT", projectId);
  const reviews = reviewsResponse?.data;
  const { data: project } = useProject(projectId);

  const { ratings, tags, total } = React.useMemo(() => {
    const aggregateRatings = {
      "Must Hug": {
        value: project?.counterMustHug || 0,
        icon: <IconReactionMustHug />,
      },
      Yay: {
        value: project?.counterYay || 0,
        icon: <IconReactionYay />,
      },
      Meh: {
        value: project?.counterMeh || 0,
        icon: <IconReactionMeh />,
      },
      Nah: {
        value: project?.counterNah || 0,
        icon: <IconReactionNah />,
      },
    } as const;
    const founder = {};
    const community = {};
    const roadmap = {};
    const art = {};

    reviews?.forEach((review) => {
      review.founderTeamTags?.forEach((tag) => addOrIncrement(founder, tag));
      review.communityTags?.forEach((tag) => addOrIncrement(community, tag));
      review.roadmapTags?.forEach((tag) => addOrIncrement(roadmap, tag));
      review.artTags?.forEach((tag) => addOrIncrement(art, tag));
    });

    return {
      ratings: aggregateRatings,
      total: Object.values(aggregateRatings).reduce(
        (sum, { value }) => sum + value,
        0
      ),
      tags: {
        founder: sortTagsMap(founder),
        community: sortTagsMap(community),
        roadmap: sortTagsMap(roadmap),
        art: sortTagsMap(art),
      },
    };
  }, [reviews, project]);

  if (!reviews || reviews?.length === 0) {
    return null;
  }

  return (
    <ReviewStats
      type="PROJECT"
      max={total}
      ratings={ratings}
      tags={tags}
      {...props}
    />
  );
}

export { ProjectReviewStats };
